import Button from "../../Button/Button";
import Icon from "../../Icon/Icon";
import {
  AromaModalVariantProps,
  aromaModal,
} from "@zenchef/styled-system/recipes";
import { Dialog, Portal, PortalProps } from "@ark-ui/react";
import { Box } from "@zenchef/styled-system/jsx";
import Footer, { FooterExposedProps } from "../Footer";
import { CSSProperties, PropsWithChildren, ReactNode } from "react";
import Header, { HeaderInternalProps } from "../Header";
import { IconName } from "@zenchef/icomoon";
import { cx } from "@zenchef/styled-system/css";
import { token } from "@zenchef/styled-system/tokens";
import { useWindowSize } from "../../../utils";

export interface ModalHint {
  /**
   * Hint text will shown in the footer/header depending on size variant
   */
  hint?: string;
  /**
   * Icon to show next to the hint
   * @default "help-question-circle"
   */
  hintIcon?: IconName;
}

export interface ModalRootProps
  extends Omit<Dialog.RootProps, "id" | "ids">,
    AromaModalVariantProps,
    PropsWithChildren {
  /**
   * custom className props to over the style
   */
  className?: string;
  maxHeight?: number;
  /**
   * Any react node to use as Trigger to open the modal
   *(see ControlledDrawer story if trigger should be placed somewhere else)
   */
  trigger?: React.ReactNode;
  /**
   * Z-index of the modal, the backdrop will be zIndex - 1
   */
  zIndex?: string;
  /**
   * Whether to show overlay behind the modal
   * @default true
   */
  onOverlay?: boolean;
  /**
   * By default, the modal will be rendered at the level of html Body, if you need more control over this, use portal property.
   */
  portal?: PortalProps;
  /**
   * custom content className props to over the style
   */
}

export interface ModalHeaderProps
  extends Omit<HeaderInternalProps, "classes">,
    AromaModalVariantProps {}

export interface ModalFooterProps
  extends FooterExposedProps,
    ModalHint,
    AromaModalVariantProps {
  /**
   * custom footer className props to over the style
   */
  footerClassName?: string;
  /**
   * Custom element in the footer
   */
  footer?: ReactNode;
}

export interface ModalProps
  extends ModalRootProps,
    ModalFooterProps,
    Omit<ModalHeaderProps, "closeTrigger"> {
  contentClassName?: string;
}

export function ModalCloseTrigger() {
  return (
    <Dialog.CloseTrigger asChild={true}>
      <Button iconOnly hierarchy="secondary-subtler">
        <Icon name="x" />
      </Button>
    </Dialog.CloseTrigger>
  );
}

interface ModalBodyProps extends PropsWithChildren, AromaModalVariantProps {
  contentClassName?: string;
}

export function ModalBody({
  children,
  contentClassName,
  ...props
}: ModalBodyProps) {
  const [aromaModalVariantProps] = aromaModal.splitVariantProps(props);

  const classes = aromaModal(aromaModalVariantProps);
  return <Box className={cx(classes.body, contentClassName)}>{children}</Box>;
}

export function ModalHeader({
  headerAction,
  title,
  backAction,
  hint,
  hintIcon,
  indicator,
  ...props
}: ModalHeaderProps) {
  const [aromaModalVariantProps] = aromaModal.splitVariantProps(props);

  const classes = aromaModal(aromaModalVariantProps);
  const isFullSizeVariant = aromaModalVariantProps.size === "full";

  return (
    <Header
      classes={classes}
      headerAction={headerAction}
      title={title}
      backAction={backAction}
      hint={isFullSizeVariant ? hint : undefined}
      hintIcon={hintIcon}
      closeTrigger={<ModalCloseTrigger />}
      indicator={indicator}
    />
  );
}

export function ModalFooter({
  footerClassName,
  primaryAction,
  secondaryAction,
  hint,
  hintIcon,
  footer,
  ...props
}: ModalFooterProps) {
  const [aromaModalVariantProps] = aromaModal.splitVariantProps(props);

  const classes = aromaModal(aromaModalVariantProps);
  const isFullSizeVariant = aromaModalVariantProps.size === "full";
  return (
    <Footer
      classes={classes}
      className={cx(footerClassName)}
      content={footer}
      primaryAction={primaryAction}
      secondaryAction={secondaryAction}
      hint={isFullSizeVariant ? undefined : hint}
      hintIcon={hintIcon}
      fullWidth={isFullSizeVariant}
    />
  );
}

export function ModalRoot({
  children,
  lazyMount = true,
  unmountOnExit = true,
  trapFocus = false,
  trigger,
  onOverlay = true,
  zIndex,
  portal,
  closeOnInteractOutside = onOverlay ?? true,
  className,
  maxHeight,
  ...props
}: ModalRootProps) {
  const { width: windowWidth } = useWindowSize();
  const isMobileScreen = windowWidth <= parseInt(token("breakpoints.xs"), 10);

  const [aromaModalVariantProps, restProps] = aromaModal.splitVariantProps({
    ...props,
    size: isMobileScreen ? "full" : props.size,
    stackedActions: isMobileScreen ? false : props.stackedActions,
  });

  const classes = aromaModal(aromaModalVariantProps);

  const cssVars = {
    "--modal-z-index": zIndex,
    "--backdrop-z-index": String(Number(zIndex) - 1),
    "--modal-max-height": maxHeight ? `${maxHeight}px` : "calc(100% - 80px)",
  } as CSSProperties;

  return (
    <Dialog.Root
      lazyMount={lazyMount}
      unmountOnExit={unmountOnExit}
      trapFocus={trapFocus}
      closeOnInteractOutside={closeOnInteractOutside}
      {...restProps}
    >
      {trigger && <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>}
      <Portal {...portal}>
        {onOverlay ? (
          <Dialog.Backdrop className={classes.backdrop} style={cssVars} />
        ) : null}
        <Dialog.Positioner className={classes.positioner} style={cssVars}>
          <Dialog.Content className={cx(classes.content, className)}>
            {children}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
